import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import agendaItemMapper from "@/mappers/agenda/agendaItemMapper";
import eventStore, { setOpenedEvent } from "@/stores/eventStore";
import {
    AgendaItem,
    useAgenda,
    useSdkEvent,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shallow, useSelector } from "@tanstack/react-store";
import useFetchErrorToast from "../utils/useFetchErrorToast";

// Microsoft Graph calendarView rejects ISO strings with milliseconds (HTTP 400)
const toGraphDateTime = (date: Date) => `${date.toISOString().split(".")[0]}Z`;

type DomainAgendaItem = ReturnType<typeof agendaItemMapper>;

/**
 * Agenda events for one time window, cached per window.
 *
 * @param startDate - inclusive start of the window
 * @param endDate - inclusive end of the window
 */
const useGetAgendaEvents = (startDate: Date, endDate: Date) => {
    const agenda = useAgenda();
    const showFetchErrorToast = useFetchErrorToast();
    const openedEvent = useSelector(eventStore, (state) => state.openedEvent, {
        compare: shallow,
    });
    const queryClient = useQueryClient();

    const windowKey = queryKeys.agendaEvents.window(
        toGraphDateTime(startDate),
        toGraphDateTime(endDate),
    );

    const agendaEvents = useQuery({
        queryKey: windowKey,
        staleTime: STALE_TIME.realtime,
        queryFn: async (): Promise<DomainAgendaItem[]> => {
            try {
                const events = await agenda.getUserAgenda(
                    toGraphDateTime(startDate),
                    toGraphDateTime(endDate),
                );

                return events.map(agendaItemMapper);
            } catch (error) {
                console.error("Error fetching agenda events", error);
                showFetchErrorToast(error, {
                    queryKey: windowKey,
                    titleTranslationKey: "errors.agenda.get",
                    onRetry: () =>
                        queryClient.refetchQueries({ queryKey: windowKey }),
                });

                // Sans le throw, l'echec passe pour un succes vide et l'agenda se vide.
                throw error;
            }
        },
    });

    const handleAgendaItemUpdated = (updatedAgendaItem: AgendaItem) => {
        const updatedItem = agendaItemMapper(updatedAgendaItem);

        queryClient.setQueriesData<DomainAgendaItem[]>(
            { queryKey: queryKeys.agendaEvents.all() },
            (events) =>
                events?.map((event) =>
                    event.id === updatedItem.id ? updatedItem : event,
                ),
        );

        if (openedEvent && openedEvent.id === updatedItem.id) {
            setOpenedEvent(updatedItem);
        }
    };

    const handleAgendaItemNewItem = (newAgendaItem: AgendaItem) => {
        const newItem = agendaItemMapper(newAgendaItem);

        // The window bounds live in the query key, so each cached window can be checked
        // against the new item without refetching it.
        queryClient
            .getQueriesData<DomainAgendaItem[]>({
                queryKey: queryKeys.agendaEvents.all(),
            })
            .forEach(([key, events]) => {
                const [, windowStart, windowEnd] = key as [
                    string,
                    string?,
                    string?,
                ];

                if (!events || !windowStart || !windowEnd) {
                    return;
                }

                if (
                    newItem.start < new Date(windowStart) ||
                    newItem.start > new Date(windowEnd)
                ) {
                    return;
                }

                queryClient.setQueryData<DomainAgendaItem[]>(key, [
                    ...events.filter((event) => event.id !== newItem.id),
                    newItem,
                ]);
            });
    };

    useSdkEvent("agenda-item-updated", handleAgendaItemUpdated);
    useSdkEvent("agenda-item-new-item", handleAgendaItemNewItem);

    return agendaEvents;
};

export default useGetAgendaEvents;
